// src/components/tour/TourOverlay.jsx
// Shared premium tour UI components used by both Commissions tour and Welcome onboarding tour
import React from 'react';
import { CheckCircle, X } from 'lucide-react';

// Confetti Celebration Component
export const ConfettiCelebration = () => {
  const particles = React.useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      color: ['#fbbf24', '#f59e0b', '#60a5fa', '#f472b6', '#34d399', '#a78bfa', '#fb923c'][i % 7],
      tx: (Math.random() - 0.5) * 400,
      ty: -(Math.random() * 250 + 80),
      rot: Math.random() * 720 - 360,
      delay: Math.random() * 0.4,
      duration: 1.8 + Math.random() * 1.2,
      size: 6 + Math.random() * 8,
      shape: i % 3,
    }));
  }, []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute left-1/2 top-1/2"
          style={{
            width: p.shape === 2 ? p.size * 2.5 : p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 1 ? '50%' : '2px',
            '--tx': `${p.tx}px`,
            '--ty': `${p.ty}px`,
            '--rot': `${p.rot}deg`,
            animation: `confetti-burst ${p.duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
};

// Tour Step Dot Indicators
export const TourDots = ({ current, total }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all duration-300 ${
          i === current ? 'w-5 bg-amber-500' : i < current ? 'w-1.5 bg-amber-300' : 'w-1.5 bg-gray-200'
        }`}
      />
    ))}
  </div>
);

// Tour Splash Screen (Welcome) — accepts icon as prop
export const TourSplash = ({ stepData, totalSteps, onNext, onDismiss, icon, skipLabel = 'Skip tour', nextLabel = "Let's Go →", gradientFrom = 'from-amber-400', gradientVia = 'via-amber-500', gradientTo = 'to-amber-600' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onDismiss} />
    <div className="relative w-[420px] max-w-[90vw] rounded-2xl shadow-2xl overflow-hidden animate-tour-splash-enter" role="dialog" aria-label="Feature tour welcome">
      <div className={`h-36 bg-gradient-to-br ${gradientFrom} ${gradientVia} ${gradientTo} flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute -top-10 -left-10 w-36 h-36 bg-white/10 rounded-full" />
        <div className="absolute -bottom-14 -right-14 w-44 h-44 bg-white/10 rounded-full" />
        <div className="absolute top-4 right-8 w-16 h-16 bg-white/5 rounded-full" />
        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20">
          {icon}
        </div>
      </div>
      <div className="bg-white p-7 text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{stepData.title}</h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">{stepData.description}</p>
        <TourDots current={0} total={totalSteps} />
        <div className="flex gap-3 justify-center mt-6">
          <button onClick={onDismiss} className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors">
            {skipLabel}
          </button>
          <button onClick={onNext} className="px-7 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.03] active:scale-[0.98]">
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Tour Celebration Screen (Completion) — accepts icon and button props
export const TourCelebration = ({ stepData, onDismiss, icon, buttonLabel = 'Start Tracking →', gradientFrom = 'from-emerald-400', gradientVia = 'via-emerald-500', gradientTo = 'to-teal-500' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
    <ConfettiCelebration />
    <div className="relative w-[420px] max-w-[90vw] rounded-2xl shadow-2xl overflow-hidden animate-tour-splash-enter z-10" role="dialog" aria-label="Tour complete">
      <div className={`h-36 bg-gradient-to-br ${gradientFrom} ${gradientVia} ${gradientTo} flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute -top-10 -left-10 w-36 h-36 bg-white/10 rounded-full" />
        <div className="absolute -bottom-14 -right-14 w-44 h-44 bg-white/10 rounded-full" />
        <div className="absolute top-4 right-8 w-16 h-16 bg-white/5 rounded-full" />
        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20">
          {icon || <CheckCircle className="w-8 h-8 text-white" />}
        </div>
      </div>
      <div className="bg-white p-7 text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{stepData.title}</h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">{stepData.description}</p>
        <button onClick={onDismiss} className="px-7 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.03] active:scale-[0.98]">
          {buttonLabel}
        </button>
      </div>
    </div>
  </div>
);

// Tour Spotlight Tooltip — positioned tooltip with SVG spotlight overlay
export const TourSpotlight = ({ stepData, stepIndex, totalSteps, onNext, onBack, onDismiss, maskId = 'tour-spotlight-mask' }) => {
  const [spotRect, setSpotRect] = React.useState(null);
  const [bubblePos, setBubblePos] = React.useState({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = React.useState(null);
  const [interactive, setInteractive] = React.useState(false);
  const bubbleRef = React.useRef(null);
  const padding = 10;
  const { target, position = 'bottom' } = stepData;

  // Block all pointer events for 500ms after mounting or step change
  // This prevents click-through from the previous overlay
  React.useEffect(() => {
    setInteractive(false);
    const t = setTimeout(() => setInteractive(true), 500);
    return () => clearTimeout(t);
  }, [stepIndex]);

  React.useEffect(() => {
    const computePosition = () => {
      const el = document.querySelector(`[data-tour="${target}"]`);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setSpotRect({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });

      const bubble = bubbleRef.current;
      const bw = bubble?.offsetWidth || 380;
      const bh = bubble?.offsetHeight || 220;
      let top, left;
      if (position === 'bottom') {
        top = rect.bottom + padding + 14;
        left = rect.left + rect.width / 2 - bw / 2;
      } else if (position === 'top') {
        top = rect.top - bh - padding - 14;
        left = rect.left + rect.width / 2 - bw / 2;
      } else if (position === 'right') {
        top = rect.top + rect.height / 2 - bh / 2;
        left = rect.right + padding + 14;
      } else {
        top = rect.top + rect.height / 2 - bh / 2;
        left = rect.left - bw - padding - 14;
      }
      const clampedLeft = Math.max(12, Math.min(left, window.innerWidth - bw - 12));
      top = Math.max(12, Math.min(top, window.innerHeight - bh - 12));
      const targetCenterX = rect.left + rect.width / 2;
      const arrowX = targetCenterX - clampedLeft;
      const clampedArrow = Math.max(24, Math.min(arrowX, bw - 24));
      setArrowOffset(clampedArrow);
      setBubblePos({ top, left: clampedLeft });
    };

    const el = document.querySelector(`[data-tour="${target}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const timer = setTimeout(computePosition, 400);
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [target, position]);

  return (
    <div className="fixed inset-0 z-50" style={{ pointerEvents: 'none' }}>
      {/* SVG Spotlight Overlay */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: interactive ? 'auto' : 'none' }} onClick={interactive ? (e) => {
        // Clicking inside the spotlight area passes through to the real element
        if (spotRect) {
          const x = e.clientX, y = e.clientY;
          if (x >= spotRect.x - padding && x <= spotRect.x + spotRect.width + padding &&
              y >= spotRect.y - padding && y <= spotRect.y + spotRect.height + padding) {
            // Temporarily hide SVG pointer events so elementFromPoint finds the real element
            e.currentTarget.style.pointerEvents = 'none';
            const realEl = document.elementFromPoint(x, y);
            e.currentTarget.style.pointerEvents = 'auto';
            if (realEl) { realEl.focus(); realEl.click(); }
            return;
          }
        }
        onDismiss();
      } : undefined}>
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotRect && (
              <rect
                x={spotRect.x - padding}
                y={spotRect.y - padding}
                width={spotRect.width + padding * 2}
                height={spotRect.height + padding * 2}
                rx="12"
                fill="black"
                style={{ transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0, 0, 0, 0.55)"
          mask={`url(#${maskId})`}
        />
        {/* Pulsing glow ring */}
        {spotRect && (
          <rect
            x={spotRect.x - padding - 3}
            y={spotRect.y - padding - 3}
            width={spotRect.width + (padding + 3) * 2}
            height={spotRect.height + (padding + 3) * 2}
            rx="14"
            fill="none"
            stroke="rgba(251, 191, 36, 0.5)"
            strokeWidth="2"
            className="animate-tour-pulse"
            style={{ transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        )}
      </svg>

      {/* Tooltip Card */}
      <div
        ref={bubbleRef}
        key={stepIndex}
        className="absolute w-[380px] max-w-[90vw] animate-tour-bubble-enter"
        style={{ top: bubblePos.top, left: bubblePos.left, pointerEvents: interactive ? 'auto' : 'none' }}
        role="dialog"
        aria-label={`Tour step ${stepIndex + 1} of ${totalSteps}`}
      >
        {/* Arrow */}
        {position === 'bottom' && arrowOffset !== null && (
          <div className="absolute -top-[7px] w-3.5 h-3.5 bg-white rotate-45 border-l border-t border-gray-200/60 z-10"
               style={{ left: arrowOffset - 7, boxShadow: '-2px -2px 4px rgba(0,0,0,0.04)' }} />
        )}
        {position === 'top' && arrowOffset !== null && (
          <div className="absolute -bottom-[7px] w-3.5 h-3.5 bg-white rotate-45 border-r border-b border-gray-200/60 z-10"
               style={{ left: arrowOffset - 7, boxShadow: '2px 2px 4px rgba(0,0,0,0.04)' }} />
        )}

        <div className="bg-white/[0.97] backdrop-blur-xl rounded-2xl border border-gray-200/60"
             style={{ boxShadow: '0 25px 60px -12px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.6) inset' }}>
          {/* Gradient accent bar */}
          <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 rounded-t-2xl" />

          <div className="p-5">
            {/* Emoji + Title */}
            <div className="flex items-start gap-3 mb-3">
              <span className="text-2xl leading-none mt-0.5">{stepData.emoji}</span>
              <div className="flex-1 min-w-0">
                <h4 className="text-[15px] font-bold text-gray-900 leading-snug">{stepData.title}</h4>
              </div>
              <button onClick={onDismiss} className="p-1 text-gray-300 hover:text-gray-500 transition-colors rounded-lg hover:bg-gray-100 -mt-0.5 -mr-1 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-[13px] font-medium text-gray-700 leading-relaxed mb-4 ml-[40px]">{stepData.description}</p>

            {/* Footer: dots + navigation */}
            <div className="flex items-center justify-between ml-[40px]">
              <TourDots current={stepIndex} total={totalSteps} />
              <div className="flex items-center gap-2">
                {stepIndex > 1 && (
                  <button onClick={onBack} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    Back
                  </button>
                )}
                <button onClick={onNext} className="px-4 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.98] transition-all">
                  {stepIndex === totalSteps - 2 ? 'Finish' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tour Dispatcher — routes to splash, spotlight, or celebration
export const TourBubble = ({ stepData, stepIndex, totalSteps, onNext, onBack, onDismiss, splashIcon, celebrationIcon, splashProps = {}, celebrationProps = {}, maskId }) => {
  if (stepData.type === 'splash') return <TourSplash stepData={stepData} totalSteps={totalSteps} onNext={onNext} onDismiss={onDismiss} icon={splashIcon} {...splashProps} />;
  if (stepData.type === 'celebration') return <TourCelebration stepData={stepData} onDismiss={onDismiss} icon={celebrationIcon} {...celebrationProps} />;
  return <TourSpotlight stepData={stepData} stepIndex={stepIndex} totalSteps={totalSteps} onNext={onNext} onBack={onBack} onDismiss={onDismiss} maskId={maskId} />;
};
